import { BASE_URL } from '../config/Config';

const LEGACY_CACHE_REFRESH_KEY = 'thinkstyne_legacy_cache_refresh_v1';
const BUILD_ID_STORAGE_KEY = 'thinkstyne_last_seen_build_id';
const BUILD_REFRESH_SESSION_PREFIX = 'thinkstyne_build_cache_refresh_done_';
const PWA_SERVICE_WORKER_FILE = '/pwa-service-worker.js';

const canUseStorage = (storage) => {
  try {
    storage.setItem('__cache_test__', '1');
    storage.removeItem('__cache_test__');
    return true;
  } catch (error) {
    return false;
  }
};

const getStorageValue = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const setStorageValue = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch {
    // Ignore storage failures; cache refresh should remain best-effort.
  }
};

const getDeploymentVersionUrl = () => {
  const base = (BASE_URL || '').replace(/\/$/, '');
  const path = '/api/accounts/deployment-version/';
  return `${base}${path}?_=${Date.now()}`;
};

async function fetchCurrentBuildId() {
  try {
    const response = await fetch(getDeploymentVersionUrl(), {
      method: 'GET',
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });
    if (!response.ok) return '';
    const body = await response.json();
    return body?.build_id ? String(body.build_id) : '';
  } catch (error) {
    console.warn('[CacheRefresh] Could not check deployment version:', error?.message);
    return '';
  }
}

async function deleteBrowserCaches() {
  if (!('caches' in window)) return false;
  const cacheNames = await window.caches.keys();
  await Promise.all(cacheNames.map((cacheName) => window.caches.delete(cacheName)));
  return cacheNames.length > 0;
}

async function unregisterServiceWorkers({ includePwa = false } = {}) {
  if (!('serviceWorker' in navigator)) return false;
  const registrations = await navigator.serviceWorker.getRegistrations();
  const targetRegistrations = includePwa
    ? registrations
    : registrations.filter(
        (registration) => !String(registration.active?.scriptURL || '').endsWith(PWA_SERVICE_WORKER_FILE)
      );
  await Promise.all(targetRegistrations.map((registration) => registration.unregister()));
  return targetRegistrations.length > 0;
}

async function refreshCachesForNewBuild() {
  if (isReloadExempt()) return false;
  const localStorageAvailable = canUseStorage(window.localStorage);
  const sessionStorageAvailable = canUseStorage(window.sessionStorage);
  if (!localStorageAvailable) return false;

  const currentBuildId = await fetchCurrentBuildId();
  if (!currentBuildId) return false;

  const previousBuildId = getStorageValue(window.localStorage, BUILD_ID_STORAGE_KEY);
  if (!previousBuildId) {
    setStorageValue(window.localStorage, BUILD_ID_STORAGE_KEY, currentBuildId);
    return false;
  }

  if (previousBuildId === currentBuildId) return false;

  const refreshSessionKey = `${BUILD_REFRESH_SESSION_PREFIX}${currentBuildId}`;
  const alreadyRefreshedForBuild = sessionStorageAvailable
    ? getStorageValue(window.sessionStorage, refreshSessionKey) === 'done'
    : false;

  setStorageValue(window.localStorage, BUILD_ID_STORAGE_KEY, currentBuildId);

  if (alreadyRefreshedForBuild) return false;

  try {
    await unregisterServiceWorkers({ includePwa: true });
  } catch (error) {
    console.warn('[CacheRefresh] Could not unregister service workers for new build:', error?.message);
  }

  try {
    await deleteBrowserCaches();
  } catch (error) {
    console.warn('[CacheRefresh] Could not clear caches for new build:', error?.message);
  }

  if (sessionStorageAvailable) {
    setStorageValue(window.sessionStorage, refreshSessionKey, 'done');
  }

  window.location.reload();
  return true;
}

// Pages where a reload would re-consume a one-time token and break the flow.
const RELOAD_EXEMPT_PATHS = ['/verify-email', '/reset-password', '/accept-invite', '/verify-parent-consent'];

const isReloadExempt = () =>
  RELOAD_EXEMPT_PATHS.some((p) => window.location.pathname.startsWith(p));

export async function clearLegacyWebAppCaches() {
  if (typeof window === 'undefined') return;
  if (isReloadExempt()) return;

  const canUseSessionStorage = canUseStorage(window.sessionStorage);

  const alreadyRefreshed = canUseSessionStorage
    ? window.sessionStorage.getItem(LEGACY_CACHE_REFRESH_KEY) === 'done'
    : false;

  if (alreadyRefreshed) {
    await refreshCachesForNewBuild();
    return;
  }

  let removedServiceWorker = false;
  let clearedCaches = false;

  try {
    removedServiceWorker = await unregisterServiceWorkers({ includePwa: false });
  } catch (error) {
    console.warn('[CacheRefresh] Could not unregister service workers:', error?.message);
  }

  try {
    clearedCaches = await deleteBrowserCaches();
  } catch (error) {
    console.warn('[CacheRefresh] Could not clear browser caches:', error?.message);
  }

  if (canUseSessionStorage) {
    window.sessionStorage.setItem(LEGACY_CACHE_REFRESH_KEY, 'done');
  }

  if (removedServiceWorker || clearedCaches || navigator.serviceWorker?.controller) {
    window.location.reload();
    return;
  }

  await refreshCachesForNewBuild();
}
